import { Overlay, OverlayRef } from "@angular/cdk/overlay";
import { TemplatePortal } from "@angular/cdk/portal";
import { AsyncPipe, NgClass, NgTemplateOutlet } from "@angular/common";
import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  inject,
  OnDestroy,
  OnInit,
  TemplateRef,
  ViewChild,
  ViewContainerRef,
  ViewEncapsulation,
} from "@angular/core";
import {
  FormsModule,
  ReactiveFormsModule,
  UntypedFormBuilder,
  UntypedFormGroup,
  Validators,
} from "@angular/forms";
import { MatButton, MatButtonModule } from "@angular/material/button";
import { MatFormFieldModule } from "@angular/material/form-field";
import { MatIconModule } from "@angular/material/icon";
import { MatInputModule } from "@angular/material/input";
import { MatSlideToggleModule } from "@angular/material/slide-toggle";
import { MatTooltipModule } from "@angular/material/tooltip";
import { RouterLink } from "@angular/router";
import { ShortcutsService } from "app/layout/common/shortcuts/shortcuts.service";
import { Observable, Subject, takeUntil } from "rxjs";
import { Shortcut, ShortcutModel } from "./shortcut.model";
import { DavesaConfirmationService } from "@davesa/services/confirmation";
import { LocalV2Service } from "app/core/services/v2-refactored/local-v2.service";
import { IconPickerComponent } from '@davesa/components/icon-picker/icon-picker.component';

@Component({
    selector: "shortcuts",
    templateUrl: "./shortcuts.component.html",
    encapsulation: ViewEncapsulation.None,
    changeDetection: ChangeDetectionStrategy.OnPush,
    exportAs: "shortcuts",
    imports: [
        MatButtonModule,
        MatIconModule,
        MatTooltipModule,
        NgClass,
        NgTemplateOutlet,
        RouterLink,
        FormsModule,
        ReactiveFormsModule,
        MatFormFieldModule,
        MatInputModule,
        MatSlideToggleModule,
        IconPickerComponent
    ]
})
export class ShortcutsComponent implements OnInit, OnDestroy {
  private _localV2Service = inject(LocalV2Service);
  @ViewChild("shortcutsOrigin") private _shortcutsOrigin: MatButton;
  @ViewChild("shortcutsPanel") private _shortcutsPanel: TemplateRef<any>;

  mode: "view" | "modify" | "add" | "edit" = "view";
  shortcutForm: UntypedFormGroup;
  shortcuts: Shortcut[];
  shortcuts$: Observable<Shortcut[]>;
  private _overlayRef: OverlayRef;
  private _unsubscribeAll: Subject<any> = new Subject<any>();
  selectedIcon: any = ['davesa', 'davesa-c']
  defaultIcon = `${this.selectedIcon[0]}:${this.selectedIcon[1]}`;
  iconSize: any = 'icon-size-6';

  /**
   * Constructor
   */
  constructor(
    private _changeDetectorRef: ChangeDetectorRef,
    private _formBuilder: UntypedFormBuilder,
    private _shortcutsService: ShortcutsService,
    private _overlay: Overlay,
    private _viewContainerRef: ViewContainerRef,
    private _davesaConfirmationService: DavesaConfirmationService
  ) {
    this._localV2Service.loadFromStorage();
  }

  // -----------------------------------------------------------------------------------------------------
  // @ Lifecycle hooks
  // -----------------------------------------------------------------------------------------------------

  /**
   * On init
   */
  ngOnInit(): void {
    this._shortcutsService.init();
    // Initialize the form
    this.shortcutForm = this._formBuilder.group({
      oid: [null],
      id: [null],
      label: ["", Validators.required],
      description: [""],
      icon: [this.defaultIcon, Validators.required],
      link: ["", Validators.required],
      user_router_link: [false],
    });
    
    // Get the shortcuts
    this.shortcuts$ = this._shortcutsService.shortcuts$;
    this._shortcutsService.shortcuts$
      .pipe(takeUntil(this._unsubscribeAll))
      .subscribe((shortcuts: Shortcut[]) => {
        // Load the shortcuts
        this.shortcuts = shortcuts;
          // Mark for check
        this._changeDetectorRef.markForCheck();
      });
  }

  /**
   * On destroy
   */
  ngOnDestroy(): void {
    // Unsubscribe from all subscriptions
    this._unsubscribeAll.next(null);
    this._unsubscribeAll.complete();

    // Dispose the overlay
    if (this._overlayRef) {
      this._overlayRef.dispose();
    }
  }

  // -----------------------------------------------------------------------------------------------------
  // @ Public methods
  // -----------------------------------------------------------------------------------------------------

  /**
   * Open the shortcuts panel
   */
  openPanel(): void {
    this._shortcutsService.init();

    // Return if the shortcuts panel or its origin is not defined
    if (!this._shortcutsPanel || !this._shortcutsOrigin) {
      return;
    }

    // Make sure to start in 'view' mode
    this.mode = "view";

    // Create the overlay if it doesn't exist
    if (!this._overlayRef) {
      this._createOverlay();
    }

    // Attach the portal to the overlay
    this._overlayRef.attach(
      new TemplatePortal(this._shortcutsPanel, this._viewContainerRef)
    );
  }

  /**
   * Close the shortcuts panel
   */
  closePanel(): void {
    this._overlayRef.detach();
  }

  /**
   * Change the mode
   */
  changeMode(mode: "view" | "modify" | "add" | "edit"): void {
    // Change the mode
    this.mode = mode;
  }

  /**
   * Prepare for a new shortcut
   */
  newShortcut(): void {
    // Reset the form
    this.shortcutForm.reset();

    // Enter the add mode
    this.mode = "add";
  }

  /**
   * Edit a shortcut
   */
//   updateShortcut(shortcut: Shortcut): void {
//     this._shortcutsService
//       .updateShortcut(shortcut.id, shortcut)
//       .subscribe((updatedShortcuts: Shortcut) => {
//         this.shortcuts = updatedShortcuts;
//         this._changeDetectorRef.markForCheck();
//         // Enter the edit mode
//         this.mode = "edit";
//       });
//   }

//   createShortcut(): void {
//     const newShortcut: Shortcut = this.shortcutForm.value;
//     newShortcut.id = uuidv4().toString();

//     // Assuming 'createShortcut' in your service handles both creation and update
//     this._shortcutsService
//       .createShortcut(newShortcut)
//       .subscribe((updatedShortcuts: Shortcut) => {
//         this.shortcuts = updatedShortcuts;
//         this._changeDetectorRef.markForCheck();
//       });

//     // Go back to modify mode
//     this.mode = "modify";
//   }

  /**
   * Delete shortcut
   */
  deleteShortcut(): void {
    // Get the data from the form
    const shortcut = this.shortcutForm.value;

    // Delete
    this._shortcutsService.deleteShortcut(shortcut.id).subscribe();

    // Go back the modify mode
    this.mode = "modify";
  }

  /**
   * Save shortcut
   */
  save(): void {
    // Get the data from the form
    const shortcut = this.shortcutForm.value;
    const userRouterLinkValue =
      this.shortcutForm.get("user_router_link")?.value;
    if (userRouterLinkValue === true) {
      this.shortcutForm.get("user_router_link")?.setValue(1);
    } else {
      this.shortcutForm.get("user_router_link")?.setValue(0);
    }
    // If there is an id, update it...
    if (shortcut.oid) {
      delete this.shortcutForm.value.id;
      this._shortcutsService
        .updateShortcut(shortcut.oid, this.shortcutForm.value)
        .subscribe();
     this._changeDetectorRef.markForCheck();
    }
    // Otherwise, create a new shortcut...
    else {
      delete this.shortcutForm.value.oid;
      delete this.shortcutForm.value.id;
      this._shortcutsService
        .createShortcut(this.shortcutForm.value)
        .subscribe();
      this._changeDetectorRef.markForCheck();
    }

    // Go back the modify mode
    this.mode = "modify";
  }

  /**
   * Delete shortcut
   */
  delete(): void {
    // Get the data from the form
    const shortcut = this.shortcutForm.value;

    // Delete
    this._shortcutsService.deleteShortcut(shortcut.oid).subscribe();

    // Go back the modify mode
    this.mode = "modify";
  }

  /**
   * Edit a shortcut
   */
  editShortcut(shortcut: Shortcut): void {
    // Reset the form with the shortcut
    this.shortcutForm.reset(shortcut);

    // Enter the edit mode
    this.mode = "edit";
  }

  /**
   * Track by function for ngFor loops
   *
   * @param index
   * @param item
   */
  trackByFn(index: number, item: any): any {
    return item.id || index;
  }

  // -----------------------------------------------------------------------------------------------------
  // @ Private methods
  // -----------------------------------------------------------------------------------------------------

  /**
   * Create the overlay
   */
  private _createOverlay(): void {
    // Create the overlay
    this._overlayRef = this._overlay.create({
      hasBackdrop: true,
      backdropClass: "davesa-backdrop-on-mobile",
      scrollStrategy: this._overlay.scrollStrategies.block(),
      positionStrategy: this._overlay
        .position()
        .flexibleConnectedTo(this._shortcutsOrigin._elementRef.nativeElement)
        .withLockedPosition(true)
        .withPush(true)
        .withPositions([
          {
            originX: "start",
            originY: "bottom",
            overlayX: "start",
            overlayY: "top",
          },
          {
            originX: "start",
            originY: "top",
            overlayX: "start",
            overlayY: "bottom",
          },
          {
            originX: "end",
            originY: "bottom",
            overlayX: "end",
            overlayY: "top",
          },
          {
            originX: "end",
            originY: "top",
            overlayX: "end",
            overlayY: "bottom",
          },
        ]),
    });

    // Detach the overlay from the portal on backdrop click
    this._overlayRef.backdropClick().subscribe(() => {
      this._overlayRef.detach();
    });
  }

  deleteAll() {
    //Davesa Confirmation Dialog
    const confirmation = this._davesaConfirmationService.open({
      title: "Delete All Shortcuts",
      message: "Do you want to permanently delete all stored shortcuts?",
      icon: {
        show: true,
        name: "heroicons_outline:trash",
      },
      actions: {
        confirm: {
          show: true,
          label: "Confirm",
          color: "warn",
        },
        cancel: {
          show: true,
          label: "Cancel",
        },
      },
      dismissible: true,
    });

    // Subscribe to the confirmation dialog closed action
    confirmation.afterClosed().subscribe((result) => {
      console.log("result", result);
      if (result === "confirmed") {
        // alert("All shortcuts deleted");
        this._shortcutsService
          .deleteAllShortcuts()
          .subscribe((shortcuts: any) => {
            this.shortcuts = null;
            this._changeDetectorRef.markForCheck();
          });
      }
    });
  }

  onIconPickerSelect(data:any):void{
    console.log(data);
    this.selectedIcon = data;
    this.defaultIcon = `${this.selectedIcon[0]}:${this.selectedIcon[1]}`;
    this.shortcutForm.get('icon').setValue(this.defaultIcon);

  }

}